<template>
  <div>
    <div class="breadcrumb">
      <Breadcrumb>
        <Breadcrumb-item href="#">CRM管理</Breadcrumb-item>
        <Breadcrumb-item>销售通话记录AI</Breadcrumb-item>
      </Breadcrumb>
    </div>
    <div style="margin-top: 10px">
      <Form :label-width="85" :show-message="false">
        <Row :gutter="32">
          <Col span="6">
            <Form-item label="录音日期:">
              <DatePicker :value="defaultDate" @on-change="onDateChange" format="yyyy-MM-dd" type="daterange"
                          placement="bottom-start" placeholder="选择日期" style="width: 100%"></DatePicker>
            </Form-item>
          </Col>
          <Col span="6">
            <Form-item label="标题">
              <Input placeholder="听记标题" v-model="queryParam.fileName" clearable/>
            </Form-item>
          </Col>
          <Col span="5">
            <Form-item label="绑定人">
              <AdminUserSelect v-model="queryParam.adminUserId" placeholder="全部"></AdminUserSelect>
            </Form-item>
          </Col>
          <Col span="4">
            <Form-item label="学生ID">
              <Input placeholder="学生ID" v-model="queryParam.uid" clearable/>
            </Form-item>
          </Col>
          <Col span="3">
            <Button type="primary" :loading="searchLoading" icon="ios-search" @click="search">搜索</Button>
          </Col>
        </Row>
      </Form>
    </div>
    <List :tableColumns="tableColumns" listUrl="/crm/dviAudio/page-list"
          :params="queryParam" :onLoading="onLoading" :reload="reload"></List>

    <Modal v-model="detailVisible" :title="detailTitle" width="960" :mask-closable="false" class-name="dvi-audio-detail-modal">
      <div v-if="detailLoading" style="text-align:center;padding:40px 0;">
        <Spin size="large"></Spin>
        <div style="margin-top:8px;color:#808695;">加载中…</div>
      </div>
      <div v-else-if="detail" class="dvi-detail">
        <div class="dvi-detail-head">
          <div class="dvi-detail-title" :title="displayTitle(detail.fileName)">{{ displayTitle(detail.fileName) }}</div>
          <div class="dvi-detail-meta">
            <span>{{ detail.createTime || '—' }}</span>
            <span class="dot">·</span>
            <span>{{ formatDuration(detail.durationMs) }}</span>
            <span class="dot">·</span>
            <span>{{ detail.adminUserName || '—' }}</span>
            <span class="dot">·</span>
            <span v-if="detail.uid">学生 · {{ studentLabel(detail) }}<template v-if="detail.studentMobile"> · {{ detail.studentMobile }}</template></span>
            <span v-else>学生 · 未绑定 <a href="javascript:;" @click.stop="openBind(detail)">绑定学生</a></span>
            <Tag :color="detail.hasSummary ? 'green' : 'default'" style="margin-left:8px;">摘要{{ detail.hasSummary ? '有' : '无' }}</Tag>
            <Tag :color="detail.hasAsrClean ? 'green' : 'default'">清洗{{ detail.hasAsrClean ? '有' : '无' }}</Tag>
            <Tag :color="detail.hasTranscript ? 'blue' : 'default'">原文{{ detail.hasTranscript ? '有' : '无' }}</Tag>
            <Tag :color="detail.hasAudio ? 'blue' : 'default'">音频{{ detail.hasAudio ? '有' : '无' }}</Tag>
          </div>
        </div>

        <div class="dvi-section">
          <audio v-if="detail.storageUrl" :src="detail.storageUrl" controls style="width:100%;"></audio>
          <div v-else class="dvi-empty">暂无音频（尚未转存到七牛）</div>
        </div>

        <div class="dvi-section">
          <div class="dvi-section-title">摘要</div>
          <div v-if="detail.summaryContent" class="dvi-md track-md" v-html="summaryHtml"></div>
          <div v-else class="dvi-empty">暂无摘要</div>
        </div>

        <div class="dvi-section">
          <div class="dvi-section-head">
            <div class="dvi-section-title">AI清洗</div>
            <Button size="small" type="primary" ghost
                    :loading="aiCleanLoading"
                    :disabled="aiCleanLoading || !canAiClean"
                    @click="handleAiClean">{{ aiCleanBtnText }}</Button>
          </div>
          <div v-if="detail.asrCleanContent" class="dvi-md track-md" v-html="cleanHtml"></div>
          <div v-else class="dvi-empty">暂无 AI 清洗内容</div>
        </div>

        <div v-if="hasTodos || detail.todoError" class="dvi-section">
          <div class="dvi-section-title">待办</div>
          <div v-if="detail.todoError" class="dvi-empty" style="color:#ed4014;">{{ detail.todoError }}</div>
          <ul v-if="hasTodos" class="dvi-todo-list">
            <li v-for="(t, idx) in detail.todos" :key="idx"><span class="todo-box">☐</span>{{ t }}</li>
          </ul>
          <div v-else-if="!detail.todoError" class="dvi-empty">暂无待办</div>
        </div>

        <div class="dvi-section">
          <div class="dvi-section-head">
            <div class="dvi-section-title">原始对话</div>
            <Button v-if="chatBubbles.length" size="small" @click="toggleRoleSwap">转换角色</Button>
          </div>
          <div v-if="chatBubbles.length" class="dvi-chat">
            <div v-for="(b, idx) in chatBubbles" :key="idx" class="dvi-bubble-row" :class="b.side">
              <div class="dvi-bubble">
                <div class="dvi-bubble-meta">
                  <span class="nick">{{ b.nick }}</span>
                  <span v-if="b.timeLabel" class="time">{{ b.timeLabel }}</span>
                </div>
                <div class="dvi-bubble-text">{{ b.text }}</div>
              </div>
            </div>
          </div>
          <pre v-else-if="fallbackTranscript" class="dvi-raw">{{ fallbackTranscript }}</pre>
          <div v-else class="dvi-empty">暂无原始对话</div>
        </div>

        <Collapse simple style="margin-top:12px;">
          <Panel name="meta">元信息
            <div slot="content" class="dvi-meta-extra">
              <div>ID：{{ detail.id }}</div>
              <div>SN：{{ detail.sn || '—' }}</div>
              <div>fileId：{{ detail.fileId || '—' }}</div>
              <div>minutesId：{{ detail.minutesId || '—' }}</div>
            </div>
          </Panel>
        </Collapse>
      </div>
      <div slot="footer">
        <Button type="primary" @click="detailVisible = false">关闭</Button>
      </div>
    </Modal>

    <Modal v-model="bindVisible" title="绑定学生" width="480" :mask-closable="false" @on-cancel="closeBind">
      <Form :label-width="80">
        <Form-item label="学生ID" required>
          <Input v-model="bindForm.uidInput" placeholder="输入6位学生ID" clearable
                 :disabled="bindResolving"
                 @on-blur="resolveStudent"
                 @on-enter="resolveStudent"/>
          <Spin v-if="bindResolving" size="small" style="margin-left:8px;"></Spin>
        </Form-item>
        <Form-item label="姓名">
          <span>{{ bindForm.studentName || '—' }}</span>
        </Form-item>
        <Form-item label="电话">
          <span>{{ bindForm.studentMobile || '—' }}</span>
        </Form-item>
      </Form>
      <div slot="footer">
        <Button @click="closeBind">取消</Button>
        <Button type="primary" :disabled="!bindForm.resolvedUid" :loading="bindSaving" @click="saveBind">保存</Button>
      </div>
    </Modal>

    <Modal v-model="previewVisible" :title="previewTitle" width="760" :mask-closable="false"
           class-name="dvi-audio-preview-modal" @on-cancel="closePreview">
      <div v-if="previewLoading" style="text-align:center;padding:40px 0;">
        <Spin size="large"></Spin>
        <div style="margin-top:8px;color:#808695;">加载中…</div>
      </div>
      <div v-else-if="previewDetail" class="dvi-preview-body">
        <template v-if="previewKind === 'summary'">
          <div v-if="previewSummaryHtml" class="dvi-md track-md" v-html="previewSummaryHtml"></div>
          <div v-else class="dvi-empty">暂无摘要</div>
        </template>
        <template v-else-if="previewKind === 'clean'">
          <div v-if="previewCleanHtml" class="dvi-md track-md" v-html="previewCleanHtml"></div>
          <div v-else class="dvi-empty">暂无 AI 清洗内容</div>
        </template>
        <template v-else-if="previewKind === 'transcript'">
          <div class="dvi-section-head" style="margin-bottom:8px;">
            <div class="dvi-section-title" style="margin-bottom:0;">原始对话</div>
            <Button v-if="previewChatBubbles.length" size="small" @click="togglePreviewRoleSwap">转换角色</Button>
          </div>
          <div v-if="previewChatBubbles.length" class="dvi-chat dvi-chat-preview">
            <div v-for="(b, idx) in previewChatBubbles" :key="'p'+idx" class="dvi-bubble-row" :class="b.side">
              <div class="dvi-bubble">
                <div class="dvi-bubble-meta">
                  <span class="nick">{{ b.nick }}</span>
                  <span v-if="b.timeLabel" class="time">{{ b.timeLabel }}</span>
                </div>
                <div class="dvi-bubble-text">{{ b.text }}</div>
              </div>
            </div>
          </div>
          <pre v-else-if="previewFallbackTranscript" class="dvi-raw">{{ previewFallbackTranscript }}</pre>
          <div v-else class="dvi-empty">暂无原始对话</div>
        </template>
      </div>
      <div slot="footer">
        <Button type="primary" @click="closePreview">关闭</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import AdminUserSelect from '../common/AdminUserSelect.vue'
import { renderSimpleMarkdown } from '@/utils/simpleMarkdown'

function pad2 (n) {
  return n < 10 ? '0' + n : '' + n
}

function formatMs (ms) {
  if (ms == null || ms === '' || isNaN(ms)) return ''
  var total = Math.max(0, Math.floor(Number(ms) / 1000))
  var m = Math.floor(total / 60)
  var s = total % 60
  return m + ':' + pad2(s)
}

function formatDuration (ms) {
  if (ms == null || ms === '' || isNaN(ms)) return '—'
  var total = Math.max(0, Math.floor(Number(ms) / 1000))
  var m = Math.floor(total / 60)
  var s = total % 60
  if (m <= 0) return s + '秒'
  return m + '分' + s + '秒'
}

export default {
  name: 'DviAudioList',
  components: { AdminUserSelect },
  data () {
    const end = new Date()
    const begin = new Date(end)
    begin.setDate(end.getDate() - 6)
    const beginStr = begin.toISOString().split('T')[0]
    const endStr = end.toISOString().split('T')[0]
    return {
      reload: 0,
      searchLoading: false,
      defaultDate: [beginStr, endStr],
      queryParam: {
        beginTime: beginStr,
        endTime: endStr,
        fileName: '',
        adminUserId: null,
        uid: ''
      },
      bindVisible: false,
      bindResolving: false,
      bindSaving: false,
      bindTargetId: null,
      bindForm: {
        uidInput: '',
        resolvedUid: null,
        studentName: '',
        studentMobile: ''
      },
      detailVisible: false,
      detailLoading: false,
      detail: null,
      summaryHtml: '',
      cleanHtml: '',
      aiCleanLoading: false,
      backfillLoadingId: null,
      chatBubbles: [],
      fallbackTranscript: '',
      roleSwapped: false,
      transcriptList: [],
      speakerOrder: [],
      previewVisible: false,
      previewLoading: false,
      previewKind: '',
      previewCache: {},
      previewDetail: null,
      previewSummaryHtml: '',
      previewCleanHtml: '',
      previewChatBubbles: [],
      previewFallbackTranscript: '',
      previewRoleSwapped: false,
      previewTranscriptList: [],
      previewSpeakerOrder: [],
      tableColumns: [
        {
          title: '标题',
          minWidth: 200,
          align: 'left',
          render: (h, params) => {
            const name = params.row.fileName || '未命名听记'
            return h('span', { attrs: { title: name } }, name)
          }
        },
        {
          title: '录音时间',
          key: 'createTime',
          minWidth: 160,
          align: 'center'
        },
        {
          title: '绑定人',
          minWidth: 100,
          align: 'center',
          render: (h, params) => h('span', params.row.adminUserName || '—')
        },
        {
          title: '学生',
          minWidth: 150,
          align: 'left',
          render: (h, params) => this.renderStudentCell(h, params.row)
        },
        {
          title: '摘要',
          width: 80,
          align: 'center',
          render: (h, params) => {
            const has = !!params.row.hasSummary
            return h('Tag', {
              props: { color: has ? 'green' : 'default' },
              style: has ? { cursor: 'pointer' } : {},
              nativeOn: has ? { click: (e) => { e && e.stopPropagation && e.stopPropagation(); this.openPreview(params.row.id, 'summary') } } : {}
            }, has ? '有' : '无')
          }
        },
        {
          title: '清洗',
          width: 80,
          align: 'center',
          render: (h, params) => {
            const has = !!params.row.hasAsrClean
            return h('Tag', {
              props: { color: has ? 'green' : 'default' },
              style: has ? { cursor: 'pointer' } : {},
              nativeOn: has ? { click: (e) => { e && e.stopPropagation && e.stopPropagation(); this.openPreview(params.row.id, 'clean') } } : {}
            }, has ? '有' : '无')
          }
        },
        {
          title: '原文',
          width: 80,
          align: 'center',
          render: (h, params) => {
            const has = !!params.row.hasTranscript
            return h('Tag', {
              props: { color: has ? 'blue' : 'default' },
              style: has ? { cursor: 'pointer' } : {},
              nativeOn: has ? { click: (e) => { e && e.stopPropagation && e.stopPropagation(); this.openPreview(params.row.id, 'transcript') } } : {}
            }, has ? '有' : '无')
          }
        },
        {
          title: '音频',
          width: 180,
          align: 'center',
          render: (h, params) => {
            if (params.row.hasAudio && params.row.storageUrl) {
              return h('audio', {
                attrs: {
                  src: params.row.storageUrl,
                  controls: 'controls',
                  preload: 'none'
                },
                style: { width: '160px', height: '32px', verticalAlign: 'middle' }
              })
            }
            return h('span', { style: { color: '#c5c8ce' } }, '—')
          }
        },
        {
          title: '操作',
          width: 120,
          align: 'center',
          render: (h, params) => h('div', [
            h('a', {
              style: { marginRight: '10px' },
              on: { click: () => this.openDetail(params.row.id) }
            }, '查看'),
            h('a', {
              on: { click: () => this.handleBackfill(params.row) }
            }, '补录')
          ])
        }
      ]
    }
  },
  computed: {
    detailTitle () {
      if (!this.detail) return '通话详情'
      return '通话详情'
    },
    previewTitle () {
      if (this.previewKind === 'summary') return '摘要预览'
      if (this.previewKind === 'clean') return 'AI清洗预览'
      if (this.previewKind === 'transcript') return '原始对话预览'
      return '预览'
    },
    hasTodos () {
      return this.detail && Array.isArray(this.detail.todos) && this.detail.todos.length > 0
    },
    canAiClean () {
      if (!this.detail) return false
      if (this.detail.hasTranscript) return true
      if (this.detail.asrTranscript) return true
      return false
    },
    aiCleanBtnText () {
      if (this.detail && this.detail.asrCleanContent) return '重新清洗'
      return 'AI 处理'
    }
  },
  methods: {
    displayTitle (name) {
      return name || '未命名听记'
    },

    studentLabel (row) {
      if (!row || row.uid == null || row.uid === '') return ''
      if (row.studentName) return row.uid + '-' + row.studentName
      return String(row.uid)
    },
    renderStudentCell (h, row) {
      if (!row || row.uid == null || row.uid === '') {
        return h('div', [
          h('span', { style: { color: '#c5c8ce' } }, '未绑定'),
          h('a', {
            style: { marginLeft: '8px' },
            on: { click: () => this.openBind(row) }
          }, '绑定')
        ])
      }
      const kids = [
        h('div', this.studentLabel(row))
      ]
      if (!row.studentName) {
        kids.push(h('div', { style: { color: '#c5c8ce', fontSize: '12px' } }, '（学生信息缺失）'))
      } else if (row.studentMobile) {
        kids.push(h('div', { style: { color: '#808695', fontSize: '12px' } }, row.studentMobile))
      }
      kids.push(h('a', {
        style: { fontSize: '12px' },
        on: { click: () => this.openBind(row) }
      }, '改绑'))
      return h('div', kids)
    },
    openBind (row) {
      if (!row || !row.id) return
      this.bindTargetId = row.id
      this.bindForm = {
        uidInput: row.uid != null ? String(row.uid) : '',
        resolvedUid: null,
        studentName: '',
        studentMobile: ''
      }
      this.bindVisible = true
      if (row.uid != null) {
        this.$nextTick(() => this.resolveStudent())
      }
    },
    closeBind () {
      this.bindVisible = false
      this.bindResolving = false
      this.bindSaving = false
      this.bindTargetId = null
      this.bindForm = { uidInput: '', resolvedUid: null, studentName: '', studentMobile: '' }
    },
    resolveStudent () {
      const raw = (this.bindForm.uidInput || '').trim()
      if (!raw) {
        this.bindForm.resolvedUid = null
        this.bindForm.studentName = ''
        this.bindForm.studentMobile = ''
        return
      }
      if (!/^\d+$/.test(raw)) {
        this.$Message.error('未找到学生')
        this.bindForm.resolvedUid = null
        this.bindForm.studentName = ''
        this.bindForm.studentMobile = ''
        return
      }
      this.bindResolving = true
      this.$http.get('/crm/dviAudio/resolve-uid', { params: { uid: raw } }).then((res) => {
        this.bindResolving = false
        if (!res || res.status !== 'ok' || !res.data) {
          this.bindForm.resolvedUid = null
          this.bindForm.studentName = ''
          this.bindForm.studentMobile = ''
          this.$Message.error((res && res.msg) || '未找到学生')
          return
        }
        this.bindForm.resolvedUid = res.data.uid
        this.bindForm.studentName = res.data.studentName || ''
        this.bindForm.studentMobile = res.data.studentMobile || ''
      }).catch(() => {
        this.bindResolving = false
        this.bindForm.resolvedUid = null
        this.bindForm.studentName = ''
        this.bindForm.studentMobile = ''
        this.$Message.error('未找到学生')
      })
    },
    saveBind () {
      if (!this.bindTargetId) return
      if (!this.bindForm.resolvedUid) {
        this.$Message.warning('请输入学生ID')
        return
      }
      this.bindSaving = true
      this.$http.get('/crm/dviAudio/bind-uid', {
        params: { id: this.bindTargetId, uid: this.bindForm.resolvedUid }
      }).then((res) => {
        this.bindSaving = false
        if (!res || res.status !== 'ok') {
          this.$Message.error((res && res.msg) || '绑定失败')
          return
        }
        this.$Message.success('绑定成功')
        const boundId = this.bindTargetId
        const data = res.data || {}
        this.closeBind()
        this.reload = new Date().getTime()
        if (this.detail && this.detail.id === boundId) {
          this.detail.uid = data.uid
          this.detail.studentName = data.studentName
          this.detail.studentMobile = data.studentMobile
        }
      }).catch(() => {
        this.bindSaving = false
        this.$Message.error('绑定失败')
      })
    },
    handleBackfill (row) {
      if (!row || row.id == null) return
      if (this.backfillLoadingId != null) return
      this.backfillLoadingId = row.id
      this.$http.post('/crm/dviAudio/backfill', { id: row.id }).then((res) => {
        this.backfillLoadingId = null
        if (!res || res.code !== 200) {
          this.$Message.error((res && res.msg) || '补录失败')
          return
        }
        const data = res.data || {}
        this.$Message.success(data.message || '补录完成')
        this.reload = new Date().getTime()
        if (this.previewCache && this.previewCache[row.id]) {
          this.$delete(this.previewCache, row.id)
        }
        if (this.detail && this.detail.id === row.id) {
          this.openDetail(row.id)
        }
      }).catch(() => {
        this.backfillLoadingId = null
        this.$Message.error('补录失败')
      })
    },

    handleAiClean () {
      if (!this.detail || !this.detail.id) return
      if (!this.canAiClean) {
        this.$Message.warning('暂无原始对话，无法 AI 处理')
        return
      }
      const doRequest = () => {
        this.aiCleanLoading = true
        this.$http.post('/crm/dviAudio/ai-clean', { id: this.detail.id }).then((res) => {
          this.aiCleanLoading = false
          if (!res || res.status !== 'ok') {
            this.$Message.error((res && res.msg) || 'AI 处理失败')
            return
          }
          const md = (res.data && res.data.asrCleanContent) || ''
          this.detail.asrCleanContent = md
          this.detail.hasAsrClean = !!md
          this.cleanHtml = md ? renderSimpleMarkdown(md) : ''
          if (this.detail && this.detail.id != null) {
            this.$set(this.previewCache, this.detail.id, Object.assign({}, this.detail))
          }
          this.$Message.success('AI 处理完成')
          this.reload = new Date().getTime()
        }).catch(() => {
          this.aiCleanLoading = false
          this.$Message.error('AI 处理失败')
        })
      }
      if (this.detail.asrCleanContent) {
        this.$Modal.confirm({
          title: '确认重新清洗',
          content: '将用 AI 结果覆盖当前清洗内容，是否继续？',
          onOk: () => { doRequest() }
        })
      } else {
        doRequest()
      }
    },
    formatDuration,
    onLoading (loading) {
      this.searchLoading = loading
    },
    onDateChange (value) {
      this.queryParam.beginTime = value && value[0] ? value[0] : ''
      this.queryParam.endTime = value && value[1] ? value[1] : ''
    },
    search () {
      this.previewCache = {}
      this.reload = new Date().getTime()
    },
    openDetail (id) {
      if (!id) return
      this.closePreview()
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      this.summaryHtml = ''
      this.cleanHtml = ''
      this.aiCleanLoading = false
      this.chatBubbles = []
      this.fallbackTranscript = ''
      this.roleSwapped = false
      this.transcriptList = []
      this.speakerOrder = []
      const apply = (data) => {
        this.detailLoading = false
        this.detail = data || {}
        this.summaryHtml = this.detail.summaryContent
          ? renderSimpleMarkdown(this.detail.summaryContent)
          : ''
        this.cleanHtml = this.detail.asrCleanContent
          ? renderSimpleMarkdown(this.detail.asrCleanContent)
          : ''
        this.parseTranscript(this.detail.asrTranscript)
      }
      const cached = this.previewCache[id]
      // 补拉前后缓存易脏：无原文内容时强制重拉
      if (cached && cached.asrTranscript) {
        apply(cached)
        return
      }
      this.$http.get('/crm/dviAudio/detail', { params: { id } }).then((res) => {
        if (!res || res.status !== 'ok') {
          this.detailLoading = false
          this.$Message.error((res && res.msg) || '记录不存在')
          this.detailVisible = false
          return
        }
        const data = res.data || {}
        this.$set(this.previewCache, id, data)
        apply(data)
      }).catch(() => {
        this.detailLoading = false
        this.detailVisible = false
        this.$Message.error('请求失败')
      })
    },
    openPreview (id, kind) {
      if (!id || !kind) return
      this.previewKind = kind
      this.previewVisible = true
      this.previewLoading = true
      this.previewDetail = null
      this.previewSummaryHtml = ''
      this.previewCleanHtml = ''
      this.previewChatBubbles = []
      this.previewFallbackTranscript = ''
      this.previewRoleSwapped = false
      this.previewTranscriptList = []
      this.previewSpeakerOrder = []
      const show = (data) => {
        this.previewLoading = false
        this.previewDetail = data || {}
        this.previewSummaryHtml = this.previewDetail.summaryContent
          ? renderSimpleMarkdown(this.previewDetail.summaryContent)
          : ''
        this.previewCleanHtml = this.previewDetail.asrCleanContent
          ? renderSimpleMarkdown(this.previewDetail.asrCleanContent)
          : ''
        if (kind === 'transcript') {
          this.parsePreviewTranscript(this.previewDetail.asrTranscript)
        }
      }
      const cached = this.previewCache[id]
      const needFresh = !cached || (
        (kind === 'transcript' && !cached.asrTranscript) ||
        (kind === 'clean' && !cached.asrCleanContent) ||
        (kind === 'summary' && !cached.summaryContent)
      )
      if (cached && !needFresh) {
        show(cached)
        return
      }
      this.$http.get('/crm/dviAudio/detail', { params: { id } }).then((res) => {
        if (!res || res.status !== 'ok') {
          this.previewLoading = false
          this.previewVisible = false
          this.$Message.error((res && res.msg) || '记录不存在')
          return
        }
        const data = res.data || {}
        this.$set(this.previewCache, id, data)
        show(data)
      }).catch(() => {
        this.previewLoading = false
        this.previewVisible = false
        this.$Message.error('请求失败')
      })
    },
    closePreview () {
      this.previewVisible = false
      this.previewLoading = false
      this.previewKind = ''
      this.previewDetail = null
      this.previewSummaryHtml = ''
      this.previewCleanHtml = ''
      this.previewChatBubbles = []
      this.previewFallbackTranscript = ''
      this.previewRoleSwapped = false
      this.previewTranscriptList = []
      this.previewSpeakerOrder = []
    },
    parsePreviewTranscript (raw) {
      this.previewChatBubbles = []
      this.previewFallbackTranscript = ''
      this.previewRoleSwapped = false
      this.previewTranscriptList = []
      this.previewSpeakerOrder = []
      if (raw == null || raw === '') return
      let data = null
      try {
        data = typeof raw === 'string' ? JSON.parse(raw) : raw
      } catch (e) {
        this.previewFallbackTranscript = String(raw)
        return
      }
      let list = null
      if (Array.isArray(data)) {
        list = data
      } else if (data && Array.isArray(data.paragraphList)) {
        list = data.paragraphList
      }
      if (!list || !list.length) {
        if (typeof data === 'string') {
          this.previewFallbackTranscript = data
        } else if (data && typeof data.text === 'string') {
          this.previewFallbackTranscript = data.text
        } else {
          this.previewFallbackTranscript = typeof raw === 'string' ? raw : JSON.stringify(data, null, 2)
        }
        return
      }
      const resolveNick = (p) => {
        if (!p) return '发言人'
        if (typeof p.speakerDisplay === 'string' && p.speakerDisplay) return p.speakerDisplay
        if (p.speakerDisplay && typeof p.speakerDisplay === 'object') {
          if (p.speakerDisplay.nickName) return p.speakerDisplay.nickName
          if (p.speakerDisplay.name) return p.speakerDisplay.name
        }
        if (p.nickName) return p.nickName
        return '发言人'
      }
      const speakerKey = (p) => {
        const nick = resolveNick(p)
        if (nick && nick !== '发言人') return 'n:' + nick
        if (p.unionId) return 'u:' + p.unionId
        return 'unknown'
      }
      const speakerOrder = []
      list.forEach((p) => {
        const k = speakerKey(p)
        if (speakerOrder.indexOf(k) < 0) speakerOrder.push(k)
      })
      this.previewTranscriptList = list.map((p) => {
        let text = p.paragraph
        if (!text && Array.isArray(p.sentenceList)) {
          text = p.sentenceList.map((s) => (s && (s.sentence || s.text || s.content)) || '').filter(Boolean).join('')
        }
        if (!text) text = ''
        return {
          key: speakerKey(p),
          text,
          timeLabel: formatMs(p.startTime)
        }
      }).filter((item) => item.text)
      this.previewSpeakerOrder = speakerOrder
      this.buildPreviewBubbles()
    },
    previewRoleMetaForKey (key) {
      const idx = this.previewSpeakerOrder.indexOf(key)
      const swapped = this.previewRoleSwapped
      if (idx <= 0) {
        if (swapped) return { nick: '家长', side: 'left' }
        return { nick: '销售', side: 'right' }
      }
      if (idx === 1) {
        if (swapped) return { nick: '销售', side: 'right' }
        return { nick: '家长', side: 'left' }
      }
      return { nick: '其他', side: 'left' }
    },
    buildPreviewBubbles () {
      this.previewChatBubbles = this.previewTranscriptList.map((item) => {
        const meta = this.previewRoleMetaForKey(item.key)
        return {
          nick: meta.nick,
          text: item.text,
          side: meta.side,
          timeLabel: item.timeLabel
        }
      })
    },
    togglePreviewRoleSwap () {
      this.previewRoleSwapped = !this.previewRoleSwapped
      this.buildPreviewBubbles()
    },
    parseTranscript (raw) {
      this.chatBubbles = []
      this.fallbackTranscript = ''
      this.roleSwapped = false
      this.transcriptList = []
      this.speakerOrder = []
      if (raw == null || raw === '') return
      let data = null
      try {
        data = typeof raw === 'string' ? JSON.parse(raw) : raw
      } catch (e) {
        this.fallbackTranscript = String(raw)
        return
      }
      let list = null
      if (Array.isArray(data)) {
        list = data
      } else if (data && Array.isArray(data.paragraphList)) {
        list = data.paragraphList
      }
      if (!list || !list.length) {
        if (typeof data === 'string') {
          this.fallbackTranscript = data
        } else if (data && typeof data.text === 'string') {
          this.fallbackTranscript = data.text
        } else {
          this.fallbackTranscript = typeof raw === 'string' ? raw : JSON.stringify(data, null, 2)
        }
        return
      }
      const resolveNick = (p) => {
        if (!p) return '发言人'
        if (typeof p.speakerDisplay === 'string' && p.speakerDisplay) return p.speakerDisplay
        if (p.speakerDisplay && typeof p.speakerDisplay === 'object') {
          if (p.speakerDisplay.nickName) return p.speakerDisplay.nickName
          if (p.speakerDisplay.name) return p.speakerDisplay.name
        }
        if (p.nickName) return p.nickName
        return '发言人'
      }
      const speakerKey = (p) => {
        // 钉听记多人常共用同一 unionId，优先用 nickName/发言人 N 区分说话人
        const nick = resolveNick(p)
        if (nick && nick !== '发言人') return 'n:' + nick
        if (p.unionId) return 'u:' + p.unionId
        return 'unknown'
      }
      const speakerOrder = []
      list.forEach((p) => {
        const k = speakerKey(p)
        if (speakerOrder.indexOf(k) < 0) speakerOrder.push(k)
      })
      this.transcriptList = list.map((p) => {
        let text = p.paragraph
        if (!text && Array.isArray(p.sentenceList)) {
          text = p.sentenceList.map((s) => (s && (s.sentence || s.text || s.content)) || '').filter(Boolean).join('')
        }
        if (!text) text = ''
        return {
          key: speakerKey(p),
          text,
          timeLabel: formatMs(p.startTime)
        }
      }).filter((item) => item.text)
      this.speakerOrder = speakerOrder
      this.buildBubbles()
    },
    roleMetaForKey (key) {
      const idx = this.speakerOrder.indexOf(key)
      const swapped = this.roleSwapped
      if (idx <= 0) {
        // 第 1 人或未知：默认销售右；互换后家长左
        if (swapped) return { nick: '家长', side: 'left' }
        return { nick: '销售', side: 'right' }
      }
      if (idx === 1) {
        if (swapped) return { nick: '销售', side: 'right' }
        return { nick: '家长', side: 'left' }
      }
      return { nick: '其他', side: 'left' }
    },
    buildBubbles () {
      this.chatBubbles = this.transcriptList.map((item) => {
        const meta = this.roleMetaForKey(item.key)
        return {
          nick: meta.nick,
          text: item.text,
          side: meta.side,
          timeLabel: item.timeLabel
        }
      })
    },
    toggleRoleSwap () {
      this.roleSwapped = !this.roleSwapped
      this.buildBubbles()
    }
  }
}
</script>

<style scoped>
.dvi-detail-head { margin-bottom: 12px; }
.dvi-detail-title {
  font-size: 18px;
  font-weight: 600;
  color: #17233d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dvi-detail-meta {
  margin-top: 6px;
  color: #808695;
  font-size: 13px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.dvi-detail-meta .dot { margin: 0 6px; }
.dvi-section { margin-top: 16px; }
.dvi-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.dvi-section-head .dvi-section-title { margin-bottom: 0; }
.dvi-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #515a6e;
  margin-bottom: 8px;
}
.dvi-empty { color: #c5c8ce; font-size: 13px; padding: 8px 0; }
.dvi-md {
  background: #fafafa;
  border-radius: 8px;
  padding: 12px 16px;
  text-align: left;
  line-height: 1.5;
  font-size: 14px;
  max-height: 320px;
  overflow: auto;
}
.dvi-md /deep/ .md-table { border-collapse: collapse; width: 100%; margin: 4px 0; }
.dvi-md /deep/ .md-table th,
.dvi-md /deep/ .md-table td { border: 1px solid #e8e8e8; padding: 2px 6px; }
.dvi-md /deep/ h2,
.dvi-md /deep/ h3,
.dvi-md /deep/ h4 { margin: 6px 0 4px; font-size: 14px; }
.dvi-md /deep/ ul,
.dvi-md /deep/ ol { padding-left: 18px; margin: 4px 0; }
.dvi-md /deep/ blockquote { margin: 4px 0; padding-left: 8px; border-left: 3px solid #ddd; color: #666; }
.dvi-md /deep/ p { margin: 2px 0; }
.dvi-todo-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.dvi-todo-list li {
  padding: 6px 0;
  border-bottom: 1px dashed #e8eaec;
  font-size: 14px;
  color: #515a6e;
}
.todo-box { margin-right: 8px; color: #808695; }
.dvi-chat {
  max-height: 420px;
  overflow: auto;
  padding: 8px 4px;
  background: #fafbfc;
  border-radius: 8px;
}
.dvi-chat-preview {
  max-height: 480px;
}
.dvi-preview-body .dvi-md {
  max-height: 480px;
}
.dvi-bubble-row {
  display: flex;
  margin: 8px 0;
}
.dvi-bubble-row.left { justify-content: flex-start; }
.dvi-bubble-row.right { justify-content: flex-end; }
.dvi-bubble {
  max-width: 78%;
  border-radius: 10px;
  padding: 8px 12px;
}
.dvi-bubble-row.left .dvi-bubble { background: #f2f3f5; }
.dvi-bubble-row.right .dvi-bubble { background: #e8f3ff; }
.dvi-bubble-meta {
  font-size: 12px;
  color: #808695;
  margin-bottom: 4px;
  display: flex;
  gap: 8px;
}
.dvi-bubble-text {
  font-size: 14px;
  color: #17233d;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
}
.dvi-raw {
  background: #fafafa;
  border-radius: 8px;
  padding: 12px;
  max-height: 420px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  color: #515a6e;
}
.dvi-meta-extra {
  font-size: 12px;
  color: #808695;
  line-height: 1.8;
}
</style>



// WEBPACK FOOTER //
// DviAudioList.vue?701d1866